/**
 * VerifierMatchingStatus — post-submit "Looking for a verifier..." view.
 *
 * Polls /api/property-checks/:id every 10s until the request is assigned or
 * cancelled. Offers no longer expire, so matching can take a while when verifier
 * supply is thin: rather than dying on a hard timeout (the old "dead spinner"),
 * after a few minutes we switch to a reassuring "still searching, safe to leave"
 * message and keep polling. A cancelled request renders a clear refund terminal
 * state with CTAs to re-book or pick a specific verifier.
 */

import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress, Alert, Stack, Chip, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import propertyCheckService from '../../services/propertyCheckService';
import { colors } from '../../theme/colors';

const POLL_INTERVAL_MS = 10_000;
const SOFT_WAIT_MS = 3 * 60 * 1000; // after 3 min, soften the copy but keep polling

const REFUND_COPY = {
  no_verifier_available: {
    title: "We couldn't find a verifier",
    body: "No verifier is currently available in this area for your verification. You've been fully refunded — funds usually return in 1–3 business days.",
  },
  requested_verifier_unavailable: {
    title: "That verifier isn't available",
    body: "The verifier you chose isn't available for this verification. You've been fully refunded — please book again and pick another verifier or use auto-match.",
  },
  requested_verifier_declined: {
    title: 'Your chosen verifier declined',
    body: "The verifier you chose declined this verification. You've been fully refunded — please book again and pick another verifier or use auto-match.",
  },
  all_declined: {
    title: 'No verifier accepted',
    body: "Every available verifier declined your verification. You've been fully refunded — funds usually return in 1–3 business days.",
  },
  default: {
    title: "We couldn't match you with a verifier",
    body: "Your payment is being refunded. Funds usually return in 1–3 business days.",
  },
};

const VerifierMatchingStatus = ({ requestId, onAssigned, onCancelled, onRebook, onPickVerifier }) => {
  const [status, setStatus] = useState('pending');
  const [check, setCheck] = useState(null);
  const [error, setError] = useState(null);
  const [longWait, setLongWait] = useState(false);
  const startedAtRef = useRef(Date.now());
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!requestId) return undefined;

    const fetchOnce = async () => {
      try {
        const fresh = await propertyCheckService.get(requestId);
        setCheck(fresh);
        setStatus(fresh.status || 'pending');
        if (fresh.status === 'assigned' && onAssigned) onAssigned(fresh);
        if (fresh.status === 'cancelled') {
          if (onCancelled) onCancelled(fresh);
          clearInterval(intervalRef.current);
        }
      } catch (err) {
        setError(err?.response?.data?.error || err.message || 'Failed to fetch status');
      }
    };

    fetchOnce();
    intervalRef.current = setInterval(() => {
      if (Date.now() - startedAtRef.current > SOFT_WAIT_MS) {
        setLongWait(true);
      }
      fetchOnce();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalRef.current);
  }, [requestId, onAssigned, onCancelled]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const isPending = status === 'pending';
  const isAssigned = status === 'assigned';
  const isCancelled = status === 'cancelled';

  const cancelReason = check?.payment?.cancelReason || 'default';
  const refundCopy = REFUND_COPY[cancelReason] || REFUND_COPY.default;

  const verifiersNotified = Number(check?.matching?.verifiersNotified || 0);
  // Lower-case clause for mid-sentence use ("…taking longer — N verifiers near you have been notified").
  const notifiedClause =
    verifiersNotified > 0
      ? `${verifiersNotified} ${verifiersNotified === 1 ? 'verifier' : 'verifiers'} near you ${verifiersNotified === 1 ? 'has' : 'have'} been notified`
      : 'nearby verifiers are being notified';
  const NotifiedSentence = `${notifiedClause[0].toUpperCase()}${notifiedClause.slice(1)}`;

  let pendingTitle = 'Finding the best verifier for you…';
  let pendingBody = `${NotifiedSentence} — we're waiting for one to accept. Most checks are claimed within a few minutes, and we'll text and email you the moment someone does.`;
  if (longWait) {
    pendingTitle = 'Still searching for a verifier…';
    pendingBody = `This is taking a little longer than usual — ${notifiedClause}, and your request stays live with them until someone accepts. It's safe to leave this page; we'll text and email you the moment one does.`;
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        {isPending && <CircularProgress size={28} sx={{ color: colors.brand.primary }} />}
        {isAssigned && <CheckCircleIcon sx={{ color: colors.status?.success || 'green', fontSize: 32 }} />}
        {isCancelled && <ErrorOutlineIcon sx={{ color: colors.status?.error || 'error.main', fontSize: 32 }} />}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isPending && pendingTitle}
            {isAssigned && 'Verifier assigned!'}
            {isCancelled && refundCopy.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isPending && pendingBody}
            {isAssigned && "They'll reach out shortly. You can message them from your check details."}
            {isCancelled && refundCopy.body}
          </Typography>
        </Box>
      </Stack>

      {isCancelled && (onRebook || onPickVerifier) && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          {onRebook && (
            <Button variant="contained" onClick={onRebook}>
              Re-book this check
            </Button>
          )}
          {onPickVerifier && (
            <Button variant="outlined" onClick={onPickVerifier}>
              Choose a specific verifier
            </Button>
          )}
        </Stack>
      )}

      {check && !isCancelled && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Chip
            size="small"
            label={`Status: ${status}`}
            color={isAssigned ? 'success' : 'default'}
          />
          {check.package?.displayName && (
            <Chip size="small" label={check.package.displayName} variant="outlined" />
          )}
        </Stack>
      )}
    </Box>
  );
};

export default VerifierMatchingStatus;
